import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const chatApi = axios.create({
  baseURL: `${API_BASE_URL}/chat`,
  timeout: 30000,
});

chatApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers['ngrok-skip-browser-warning'] = '1';
  return config;
});

export interface ChatAttachment {
  url: string;
  filename: string;
  mimetype: string;
  size?: number;
}

export interface ChatStudentSummary {
  _id: string;
  firstName: string;
  lastName: string;
  email?: string;
}

export interface ChatConversation {
  _id: string;
  student: ChatStudentSummary | string;
  course?: string;
  status: 'open' | 'closed' | 'pending' | 'not-started';
  context?: {
    courseId?: string;
    courseProject?: string;
    courseTitle?: Record<string, string>;
    item?: {
      chapterId?: string;
      itemId?: string;
      itemType?: string;
      itemTitle?: Record<string, string>;
      path?: string;
    };
    updatedAt?: string;
  };
  lastMessageAt?: string;
  lastMessagePreview?: string;
  unreadByAdmin: number;
  unreadByStudent: number;
  primaryCourseId?: string;
}

export interface ChatMessage {
  _id: string;
  conversationId: string;
  senderRole: 'student' | 'admin' | 'system';
  sender?: string;
  content: string;
  attachments: ChatAttachment[];
  readByAdmin: boolean;
  readByStudent: boolean;
  readByAdminAt?: string;
  readByStudentAt?: string;
  createdAt: string;
}

export const resolveChatMediaUrl = (path: string): string => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const serverBase = API_BASE_URL.replace(/\/api\/?$/, '');
  return `${serverBase}${path.startsWith('/') ? path : `/${path}`}`;
};

export const chatAPI = {
  listConversations: async (params?: { status?: string; unreadOnly?: boolean }) => {
    const { data } = await chatApi.get<{ conversations: ChatConversation[] }>('/conversations', {
      params,
    });
    return data.conversations;
  },

  getUnreadSummary: async () => {
    const { data } = await chatApi.get<{
      totalUnread: number;
      unreadConversations: number;
    }>('/admin/unread-summary');
    return data;
  },

  getConversation: async (conversationId: string) => {
    const { data } = await chatApi.get<{
      conversation: ChatConversation;
      messages: ChatMessage[];
    }>(`/conversations/${conversationId}`);
    return data;
  },

  sendMessage: async (conversationId: string, content: string, attachments: ChatAttachment[] = []) => {
    const { data } = await chatApi.post<{ message: ChatMessage; conversation: ChatConversation }>(
      `/conversations/${conversationId}/messages`,
      { content, attachments }
    );
    return data;
  },

  markRead: async (conversationId: string) => {
    await chatApi.post(`/conversations/${conversationId}/read`);
  },

  updateStatus: async (conversationId: string, status: ChatConversation['status']) => {
    const { data } = await chatApi.patch<{ conversation: ChatConversation }>(
      `/conversations/${conversationId}/status`,
      { status }
    );
    return data.conversation;
  },

  uploadImage: async (file: File): Promise<ChatAttachment> => {
    const formData = new FormData();
    formData.append('image', file);
    const token = localStorage.getItem('admin_token');
    const { data } = await axios.post(`${API_BASE_URL}/images/single`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
        'ngrok-skip-browser-warning': '1',
      },
    });
    const image = data.data.image;
    return {
      url: image.url,
      filename: image.filename,
      mimetype: image.mimetype,
      size: image.size,
    };
  },
};

export default chatAPI;
